import {useEffect, useRef, useState} from "react";
import {gsap} from 'gsap';
import {ScrollTrigger} from "gsap/ScrollTrigger";
import {CloseIcon, MenuIcon} from "../../constants/svgIcons";
import {useTranslation} from "react-i18next";
import boneIcon from "../../assets/images/logo/rotation/bone.png";
import majLogo from "../../assets/images/logo/rotation/maj-text.png";
import {Link} from "react-router-dom";
import {navItems} from "../../constants/navDb";
import changeLanguage from "../../translation/languageSwitcher";
// import navItems from "../../constants/navDb";
gsap.registerPlugin(ScrollTrigger);

function NavLink({ href, label }) {
  return (
    <Link to={href} className="text-white/90 hover:text-white px-4 py-2 text-sm font-medium transition-colors rounded-lg hover:bg-white/10">
      {label}
    </Link>
  );
}

function MobileNavLink({ href, label, onClose, indent }) {
  return (
    <Link to={href} onClick={onClose} className={`block ${indent ? 'pl-8' : 'px-4'} py-3 text-white text-sm font-medium hover:bg-white/10 rounded-xl transition-colors`}>
      {label}
    </Link>
  );
}

const Navbar = ( ) => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const boneRef = useRef(null);
  const rotationRef = useRef(0);
  const lastScrollY = useRef(window.scrollY);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [productsOpen, setProductsOpen] = useState(false);
  const {t} = useTranslation()

  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", h);

    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const delta = currentScrollY - lastScrollY.current;  // positive = down, negative = up
      rotationRef.current += delta * 0.8;   // 0.8 = sensitivity, adjust to taste
      gsap.to(boneRef.current, {
        rotation: rotationRef.current,
        duration: 0.5,
        ease: "power2.out",
      });
      lastScrollY.current = currentScrollY;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", h);
  }, []);

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-theme-color backdrop-blur-md shadow-2xl' : 'bg-transparent'}`}
        // dir={isRTL ? 'rtl' : 'ltr'}
      >
        <div className="md:w-theme-container w-mobile-container mx-auto">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link to={'/'} className="flex items-center gap-3 group w-[150px] 2xl:w-[200px]">
              <div className="w-[30px] 2xl:w-[50px]">
                <img ref={boneRef} src={boneIcon} alt="BoneIcon"/>
              </div>
              <div className="w-[70px] 2xl:w-[100px]">
                <img src={majLogo} alt="Maj Text"/>
              </div>
            </Link>
            {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-1">
              <NavLink href="/" label={t(`common.${navItems.home.translateKey}`)} />
              <div className="relative" onMouseEnter={() => setServicesOpen(true)} onMouseLeave={() => setServicesOpen(false)}>
                <button className="text-white/90 hover:text-white px-4 py-2 text-sm font-medium transition-colors flex items-center gap-1">
                  {t('common.services')}
                  <svg className={`w-4 h-4 transition-transform ${servicesOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <polyline points="6 9 12 15 18 9"/>
                  </svg>
                </button>
                {servicesOpen && (
                  <div className="absolute top-full left-0  bg-white rounded-2xl shadow-2xl p-2 w-[300px] border border-gray-100">
                    {navItems.services.map(nav => (
                      <Link
                        key={t('common.'+ nav.translateKey)}
                        to={nav.url}
                        className="block px-4 py-3 text-gray-700 hover:bg-theme-color hover:text-white rounded-xl text-sm font-medium transition-all">
                        {t('common.' + nav.translateKey)}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
              <div className="relative" onMouseEnter={() => setProductsOpen(true)} onMouseLeave={() => setProductsOpen(false)}>
                <button className="text-white/90 hover:text-white px-4 py-2 text-sm font-medium transition-colors flex items-center gap-1">
                  {t('common.products')}
                  <svg className={`w-4 h-4 transition-transform ${productsOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <polyline points="6 9 12 15 18 9"/>
                  </svg>
                </button>
                {productsOpen && (
                  <div className="absolute top-full left-0  bg-white rounded-2xl shadow-2xl p-2 w-[300px] border border-gray-100">
                    {navItems.products.map(nav => (
                      <Link
                        key={t('common.'+ nav.translateKey)}
                        to={nav.url}
                        className="block px-4 py-3 text-gray-700 hover:bg-theme-color hover:text-white rounded-xl text-sm font-medium transition-all">
                        {t('common.' + nav.translateKey)}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
              <NavLink href="/contact" label={t('common.contact')} />
            </div>

            {/* Right side */}
            <div className="flex items-center gap-3">
              {/* Language Toggle */}
              <button
                onClick={() =>
                  changeLanguage(
                    localStorage.getItem("i18nextLng") === "en" ? "ar" : "en"
                  )
                }
                className="hidden md:flex items-center gap-2 bg-white/20 hover:bg-white/30 text-white px-4 py-2 rounded-full text-sm font-semibold transition-all border border-white/30"
              >
                {localStorage.getItem("i18nextLng") === "en" ? "عربي" : "En"}
              </button>
              <Link to="/contact" className="hidden md:block bg-white text-[#000db5] px-5 py-2 rounded-full text-sm font-bold hover:shadow-lg hover:scale-105 transition-all">
                {t('common.startProject')}
              </Link>
              {/* Mobile menu */}
              <button onClick={() => setOpen(!open)} className="md:hidden text-white p-2">
                {open ? <CloseIcon /> : <MenuIcon />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {open && (
          <div
            className="md:hidden bg-theme-color border-t border-white/20 px-4 py-6 space-y-2"
          >
            <MobileNavLink href="/" label={t(`common.${navItems.home.translateKey}`)} onClose={() => setOpen(false)} />
            <div className="pt-2 pb-1 text-white/60 text-xs uppercase tracking-widest px-4">{t('common.services')}</div>
            {navItems.services.map(nav => (
              <MobileNavLink key={nav.url} href={nav.url} label={t('common.'+nav.translateKey)} onClose={() => setOpen(false)} indent />
            ))}
            <div className="pt-2 pb-1 text-white/60 text-xs uppercase tracking-widest px-4">{t('common.products')}</div>
            {navItems.products.map(nav => (
              <MobileNavLink key={nav.url} href={nav.url} label={t('common.'+nav.translateKey)} onClose={() => setOpen(false)} indent />
            ))}
            <MobileNavLink href="/contact" label={t('common.contact')} onClose={() => setOpen(false)} />
            <div className="pt-4 border-t border-white/20 flex gap-3">
              <button className="flex-1 bg-white/20 text-white py-3 rounded-xl text-sm font-semibold text-center">
                English
              </button>
              <Link to="/contact" onClick={() => setOpen(false)} className="flex-1 bg-white text-theme-color py-3 rounded-xl text-sm font-bold text-center">
                {t('common.startProject')}
              </Link>
            </div>
          </div>
        )}
      </nav>
    </>
  );
}

export default Navbar;
